import { useEffect } from 'react';
import {
  Button, Checkbox, Icon, IconButton, TextField, Typography,
} from '@gupshup-frontend/gupshup-ts-components';
import {
  BUTTON_TYPE,
  createAppSetup,
  createId,
  isAuthentication,
  isAutofill,
  isZeroTap,
} from '../state';
import { checkPackageNameFormat, checkSignatureHashFormat } from '../validate';
import { friendlyError } from './errorMessages';

// Console's own cap (task-specified) — appsSetup has no dedicated reducer actions of its own to
// carry one alongside.
const MAX_APP_SETUPS = 5;

// Console's own input caps (task-specified) — mirror the literals in gs-ui-templates/src/
// containers/Whatsapp/CreateTemplate/Auth/AppSetup.tsx.
const PACKAGE_NAME_MAX_CHARS = 25;
// A valid hash is exactly 11 characters (SIGNATURE_HASH_REGEX in Form/validate.js) — 25 is
// intentionally looser than that. Keeping the cap at the console's own ceiling (not 11) is
// deliberate: our own format validation (checkSignatureHashFormat) already rejects anything
// that isn't 11 characters, so this isn't a validation gap, just an input-length ceiling.
const SIGNATURE_HASH_MAX_CHARS = 25;

// Mirrors the console's own ZERO_TAP_TERMS_LINK (gs-ui-templates/src/containers/Whatsapp/
// CreateTemplate/config.ts:396) — same destination, opened the same way (new tab).
const ZERO_TAP_TERMS_URL = 'https://www.whatsapp.com/legal/business-terms/';

// The one auth button the zero-tap terms checkbox reads/writes through. Duplicated from
// AuthButtonsText.js (same small-local-duplication convention as this file's own constants
// above) rather than shared — see that file for the button's own seed effect.
function otpOrPasskeyButton(state) {
  return state.buttons.find(
    (btn) => btn.type === BUTTON_TYPE.OTP || btn.type === BUTTON_TYPE.PASSKEY,
  );
}

// Auth app setup — Autofill/Zero-Tap-only card for the autofill app allowlist, plus (Zero-Tap
// only) the zero-tap terms checkbox.
const AuthAppSetupSection = ({ state, dispatch, errors, disabled }) => {
  // Structural gap this effect closes: mirrors the console's own AppSetup.tsx:39-43 mount effect
  // (`if (isAppSetupAllowed && !appsSetup.length) { onAddNewAppSetup(); }`, same dependency pair).
  // Nothing else in this form ever adds an appsSetup row -- Enterprise never returns supported_apps
  // on a GET, so prefill.js leaves appsSetup: [] on every edit AND clone, not just a fresh create.
  // Left alone, every Autofill/Zero-Tap template starts with zero rows and validate.js's own
  // required check (validateAppsSetup) can never pass. Guarded on `.length === 0` alone -- once a
  // row exists this is a no-op on every later render, including the ones caused by typing into
  // that row's own fields, same idempotency goal as AuthButtonsText.js's own button-seed effect.
  // Its condition (isAutofill || isZeroTap) is exactly this component's own render gate below, so
  // — unlike the button-seed effect — it never has to run "ahead of" an excluding early return.
  useEffect(() => {
    if ((isAutofill(state) || isZeroTap(state)) && state.appsSetup.length === 0) {
      dispatch({
        type: 'set',
        path: 'appsSetup',
        value: [createAppSetup(createId('app-setup'))],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.category.value, state.category.selectedSubcategory.value]);

  if (!isAuthentication(state) || !(isAutofill(state) || isZeroTap(state))) return null;

  const zeroTap = isZeroTap(state);
  const authButton = otpOrPasskeyButton(state);

  const handleAddAppSetup = () => {
    if (state.appsSetup.length >= MAX_APP_SETUPS) return;
    dispatch({
      type: 'set',
      path: 'appsSetup',
      value: [...state.appsSetup, createAppSetup(createId('app-setup'))],
    });
  };

  const handleRemoveAppSetup = (setupId) => {
    dispatch({
      type: 'set',
      path: 'appsSetup',
      value: state.appsSetup.filter((app) => app.setupId !== setupId),
    });
  };

  return (
    <>
      <div className="br-tpl-form-card" data-testid="bloomreach-templates-form-auth-app-setup">
        <div className="br-tpl-form-card-heading-row">
          <div className="br-tpl-form-card-heading">
            <Typography variant="subtitle1" data-testid="bloomreach-templates-form-auth-app-setup-heading">
              App Setup
            </Typography>
          </div>
        </div>
        <Typography variant="caption" className="br-tpl-form-helper-text">
          You can add upto 5 app(s)
        </Typography>

        {state.appsSetup.map((app, index) => {
          // Live per-row error wins over the submit-time aggregate — same precedence as
          // expirationTimeDisplayError in AuthMessageContent.js. Paths matched against
          // validate.js's own validateAppsSetup: `appsSetup.${index}.packageName.value` /
          // `.signatureHash.value`.
          const packageNameDisplayError = app.packageName.error
            || errors[`appsSetup.${index}.packageName.value`];
          const signatureHashDisplayError = app.signatureHash.error
            || errors[`appsSetup.${index}.signatureHash.value`];
          return (
            <div className="br-tpl-form-app-setup-row" key={app.setupId}>
              <TextField
                fullWidth
                size="small"
                label="Package Name"
                value={app.packageName.value}
                onChange={(event) => dispatch({
                  type: 'merge',
                  path: `appsSetup.${index}.packageName`,
                  value: { value: event.target.value, error: checkPackageNameFormat(event.target.value) },
                })}
                disabled={disabled}
                placeholder="Eg: com.example.myapplication"
                error={Boolean(packageNameDisplayError)}
                helperText={friendlyError(packageNameDisplayError)}
                autoComplete="off"
                inputProps={{
                  maxLength: PACKAGE_NAME_MAX_CHARS,
                  'data-testid': `bloomreach-templates-form-auth-app-setup-${index}-package-name`,
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="Signature Hash"
                value={app.signatureHash.value}
                onChange={(event) => dispatch({
                  type: 'merge',
                  path: `appsSetup.${index}.signatureHash`,
                  value: { value: event.target.value, error: checkSignatureHashFormat(event.target.value) },
                })}
                disabled={disabled}
                placeholder="Eg: K8a/AINcGX7"
                error={Boolean(signatureHashDisplayError)}
                helperText={friendlyError(signatureHashDisplayError)}
                autoComplete="off"
                inputProps={{
                  maxLength: SIGNATURE_HASH_MAX_CHARS,
                  'data-testid': `bloomreach-templates-form-auth-app-setup-${index}-signature-hash`,
                }}
              />
              <IconButton
                size="small"
                disabled={disabled || state.appsSetup.length <= 1}
                onClick={() => handleRemoveAppSetup(app.setupId)}
                data-testid={`bloomreach-templates-form-auth-app-setup-${index}-remove`}
              >
                <Icon name="trashSimple" size={18} />
              </IconButton>
            </div>
          );
        })}

        {/* Defensive backstop for validate.js's own appsSetup.length === 0 check -- should be
            unreachable given the mount effect above always seeds one row, but there's otherwise
            nowhere to show that error: with zero rows there's no row of its own to render it on. */}
        {errors.appsSetup && (
          <Typography
            variant="caption"
            color="error"
            data-testid="bloomreach-templates-form-auth-app-setup-error"
          >
            {friendlyError(errors.appsSetup)}
          </Typography>
        )}

        {/* Static guidance, not tied to any .error state — the console shows these regardless
            of validity. Straight single quotes around the symbol list in the first one, unlike
            the curly apostrophes used elsewhere in this file — verbatim from the console. */}
        <Typography
          variant="caption"
          className="br-tpl-form-helper-text"
          data-testid="bloomreach-templates-form-auth-app-setup-signature-hash-guidance"
        >
          Your signature hash must be 11 characters long and you can only use alpha numeric characters, '/', '+' and '='
        </Typography>
        <Typography
          variant="caption"
          className="br-tpl-form-helper-text"
          data-testid="bloomreach-templates-form-auth-app-setup-package-name-guidance"
        >
          Your package name is not a valid Android application ID. Per Android guidelines, an application ID must have at least two segments, separated by dots. Each segment should begin with a letter, and all characters in a segment should be alphanumeric or underscore.
        </Typography>
        {state.appsSetup.length > 1 && (
          <Typography
            variant="caption"
            className="br-tpl-form-helper-text"
            data-testid="bloomreach-templates-form-auth-app-setup-unique-pair-guidance"
          >
            Your apps must contain unique package name and signature hash pairs - When more than 1 packages are added
          </Typography>
        )}

        <Button
          size="small"
          variant="outlined"
          disabled={disabled || state.appsSetup.length >= MAX_APP_SETUPS}
          onClick={handleAddAppSetup}
          data-testid="bloomreach-templates-form-auth-app-setup-add"
        >
          Add another app
        </Button>
      </div>

      {zeroTap && (
        <div className="br-tpl-form-card" data-testid="bloomreach-templates-form-auth-zero-tap-terms">
          {/* B3: first sentence states what Meta's zero_tap_terms_accepted means, rather than
              copying the console's category-approval string (a wrong/re-used key on their side,
              task-verified) — second sentence is the actual consent, sourced from the console's
              Auth/ZeroTapAuthTerms.tsx:34-48 / en.json's its_responsible_to_ensure_customers_expect. */}
          <Checkbox
            label={(
              <>
                {'Zero-tap authentication is subject to the '}
                <a
                  href={ZERO_TAP_TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  // The label above is a native <label> (see gupshup-ts-components' Checkbox ->
                  // MUI FormControlLabel) implicitly wrapping this link and the checkbox input —
                  // not a htmlFor/id pairing. Left alone, a click anywhere inside that <label>,
                  // including on this anchor, bubbles up and the browser forwards a synthetic
                  // click to the checkbox. stopPropagation (not preventDefault, which would also
                  // cancel the anchor's own navigation) keeps the click from ever reaching the
                  // <label> ancestor, so only the link's own default action — opening the URL —
                  // still happens.
                  onClick={(event) => event.stopPropagation()}
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                  data-testid="bloomreach-templates-form-auth-zero-tap-terms-link"
                >
                  WhatsApp Business Terms of Service
                </a>
                {'. It’s your business’s responsibility to ensure its customers expect that the code will be automatically filled-in on their behalf when they choose to receive the zero-tap code through WhatsApp.'}
              </>
            )}
            checked={Boolean(authButton?.zeroTapTermsAccepted)}
            onChange={(event) => authButton && dispatch({
              type: 'updateButton',
              buttonId: authButton.buttonId,
              value: { zeroTapTermsAccepted: event.target.checked },
            })}
            disabled={disabled || !authButton}
            data-testid="bloomreach-templates-form-auth-zero-tap-terms-checkbox"
          />
          {errors['buttons.zeroTapTermsAccepted'] && (
            <Typography
              variant="caption"
              color="error"
              data-testid="bloomreach-templates-form-auth-zero-tap-terms-error"
            >
              {friendlyError(errors['buttons.zeroTapTermsAccepted'])}
            </Typography>
          )}
        </div>
      )}
    </>
  );
};

export default AuthAppSetupSection;
